import {
  useAsyncData,
  useRequestURL,
  useRoute,
  useRuntimeConfig,
} from '#imports'
import { ref, watch } from 'vue'

import {
  type CMSSectionsAPI,
  sectionBlockKeys,
} from '@backmarket/http-api/src/api-specs-content/models/cms-sections'
import {
  BCP_47_LOCALES,
  type Bcp47Locale,
} from '@backmarket/http-api/src/standards/Locale'
import type { HttpError } from '@backmarket/http-api/src/utils/HttpApiError'
import { blockValidationErrors } from '@backmarket/nuxt-layer-cms/validateBlock'
import type { ExperimentName } from '@backmarket/nuxt-module-experiments/config'
import { useExperimentValue } from '@backmarket/nuxt-module-experiments/useExperimentValue'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'

import { formatBlock } from '../helpers/formatBlock'

const PLP_AB_TEST_EXPERIMENT_BY_LOCALE_AND_SLUG: Partial<
  Record<Bcp47Locale, Record<string, ExperimentName>>
> = {
  [BCP_47_LOCALES.en_US]: {
    'apple-macbook': 'experiment.macbookPlpPageName',
    'apple-ipad': 'experiment.ipadPlpPageName',
  },
}

export function useFetchFormattedCMSSections(
  options?: Record<string, unknown>,
) {
  const { market } = useMarketplace()
  const {
    FF_CMS_ENABLE_SECTIONS_COUNTRIES = '',
    FF_CMS_ENABLE_SECTIONS_PAGES = '',
  } = useRuntimeConfig().public
  const { name, params } = useRoute()

  if (
    !name ||
    !FF_CMS_ENABLE_SECTIONS_COUNTRIES.includes(market.countryCode) ||
    !FF_CMS_ENABLE_SECTIONS_PAGES.includes(name.toString())
  ) {
    return {
      data: ref({
        id: '',
        technicalName: '',
        section1: [],
        section2: [],
        section3: [],
        section4: [],
        section5: [],
      }),
      error: ref(null),
    }
  }

  const $httpFetch = useHttpFetch()
  const { productId } = useUrlParams()
  const currentUrl = useRequestURL()
  const logger = useLogger()

  const routeSlug = params.slug?.toString() || params.slugV2?.toString()
  const locale = params.locale?.toString() as Bcp47Locale | undefined
  const slugExperimentName =
    routeSlug && locale
      ? PLP_AB_TEST_EXPERIMENT_BY_LOCALE_AND_SLUG[locale]?.[routeSlug]
      : undefined
  const slug = slugExperimentName
    ? useExperimentValue(slugExperimentName, { defaultValue: routeSlug })
    : routeSlug
  const publicId = (productId as string | undefined) || params.uuid?.toString()
  const pageName = name?.toString()

  if (!slug || !publicId || !pageName) {
    logger.error('[CMS:useFetchFormattedComponentBlock] missing params', {
      error_context: { slug, publicId, pageName },
      owners: ['bot-squad-content-front'],
    })

    return {
      data: ref({
        id: '',
        technicalName: '',
        section1: [],
        section2: [],
        section3: [],
        section4: [],
        section5: [],
      }),
      error: ref(null),
    }
  }

  const sectionsPromise = useAsyncData<CMSSectionsAPI>(
    getAsyncDataKey('cms-sections', { slug, publicId, pageName }),
    () =>
      $httpFetch('/content/page-sections/:pageName/:slug/:publicId', {
        pathParams: { pageName, slug, publicId },
        query: {},
        ...options,
      }),
    {
      transform: (cmsSections) => {
        const formattedSections: CMSSectionsAPI = {
          id: cmsSections.id,
          technicalName: cmsSections.technicalName,
          section1: [],
          section2: [],
          section3: [],
          section4: [],
          section5: [],
        }

        sectionBlockKeys.forEach((sectionIndex) => {
          const sectionBlocks = cmsSections[sectionIndex]

          if (!sectionBlocks) return

          sectionBlocks.forEach((block) => {
            const errors = blockValidationErrors(block)
            if (errors.length) return

            formattedSections[sectionIndex].push(formatBlock(block, currentUrl))
          })
        })

        return formattedSections
      },
      dedupe: 'defer',
    },
  )

  const { error } = sectionsPromise

  watch(error, () => {
    if (!error.value) return

    logger.error('[useFetchFormattedCMSSections] Failed to fetch Sections', {
      error: error.value as HttpError,
      owners: ['bot-squad-content-front'],
    })
  })

  return sectionsPromise
}
